// src/features/equipment/pages/EquipmentDetailsPage/components/EquipmentFilesCard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';

import { useEquipment } from '../../../hooks/useEquipment';
import { usePermissions } from '../../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../../hooks/mui/useSnackBar';
import { useEquipmentFolder, equipmentFolderName } from '../../../hooks/useEquipmentFolder';
import SelectFileDialog from '../../../../files/components/SelectFileDialog/SelectFileDialog';
import FileExplorer from '../../../../files/components/FileExplorer';
import { downloadFileLink } from '../../../../files/hooks/useFileUrl';
import { useFiles } from '../../../../files/hooks/useFiles';
import { useFilePicker, useUploadQueue, UploadProgressList } from '../../../../files/upload';

// Map EquipmentFileDTO to the shape FileExplorer expects
const toFileEntry = (ef) => ({
  file_id: ef.file_id,
  filename: ef.filename,
  filetype: ef.filetype,
  file_size: ef.file_size,
  uploaded_at: ef.added_at,
  // Who attached it to the equipment
  uploader_id: ef.added_by,
  // Carry the join-table ID so we can remove the link on delete
  equipment_file_id: ef.equipment_file_id,
});

export default function EquipmentFilesCard({ equipmentId, equipment }) {
  const { t } = useTranslation(['equipment', 'common', 'files']);
  const permissions = usePermissions();
  const { showSuccess, showError } = useSnackbar();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [browseFolder, setBrowseFolder] = useState(null);

  const { fetchEquipmentFiles, attachFileToEquipment, removeEquipmentFile } = useEquipment();
  const { uploadFile } = useFiles();

  const canUpdate = permissions.hasScope('equipment:update');
  // Both ways of adding files look in Filer for the equipment's folder first
  const canBrowseFiles = canUpdate && permissions.hasScope('file:read');
  const canUpload = canBrowseFiles && permissions.hasScope('file:upload');

  const findEquipmentFolder = useEquipmentFolder();

  const loadFiles = useCallback(async () => {
    setError(null);
    const result = await fetchEquipmentFiles(equipmentId);
    if (result.success) {
      setFiles((result.data.files || []).map(toFileEntry));
    } else {
      setError(t('equipment:errors.files_load_failed'));
    }
    setLoading(false);
  }, [equipmentId, fetchEquipmentFiles, t]);

  useEffect(() => {
    setLoading(true);
    loadFiles();
  }, [loadFiles]);

  // New files go to the equipment's own folder (created on first upload) and are
  // attached straight away
  const uploads = useUploadQueue({
    upload: async (file, { onUploadProgress }) => {
      const folder = await findEquipmentFolder(equipmentFolderName(equipment), { create: true });
      return uploadFile(file, { folder_id: folder?.folderId ?? null, is_system_file: false, onUploadProgress });
    },
    onUploaded: async (result) => {
      const attached = await attachFileToEquipment(equipmentId, { file_id: result.data.data.file_id });
      if (!attached.success) showError(t('equipment:errors.attach_file_failed'));
    },
    onFinished: (results) => { if (results.length) loadFiles(); },
  });
  const picker = useFilePicker({ onFiles: uploads.enqueue });

  // Picking files already in Filer: start in the equipment's folder if it has one
  const handleOpenDialog = useCallback(async () => {
    if (equipment?.name) setBrowseFolder(await findEquipmentFolder(equipmentFolderName(equipment)));
    setDialogOpen(true);
  }, [equipment, findEquipmentFolder]);

  const handleAttachFiles = useCallback(async (selectedFileIds) => {
    setAttaching(true);
    let anyFailed = false;
    for (const fileId of selectedFileIds) {
      const result = await attachFileToEquipment(equipmentId, { file_id: fileId });
      if (!result.success) anyFailed = true;
    }
    setDialogOpen(false);
    setAttaching(false);
    if (anyFailed) {
      showError(t('equipment:errors.attach_file_failed'));
    } else {
      showSuccess(t('equipment:messages.file_attached'));
    }
    await loadFiles();
  }, [equipmentId, attachFileToEquipment, loadFiles, showSuccess, showError, t]);

  // "Delete" from the preview and "remove" from the menu both just unlink the
  // file from this equipment; the file stays in Filer
  const handleFileAction = useCallback(async (action, file) => {
    if (action === 'delete' || action === 'remove') {
      const result = await removeEquipmentFile(equipmentId, file.equipment_file_id);
      if (result.success) {
        showSuccess(t('equipment:messages.file_removed'));
        await loadFiles();
      } else {
        showError(t('equipment:errors.remove_file_failed'));
      }
    } else if (action === 'download') {
      downloadFileLink(file.file_id, { filename: file.filename });
    }
  }, [equipmentId, removeEquipmentFile, loadFiles, showSuccess, showError, t]);

  const alreadyAttachedFileIds = files.map(f => f.file_id);
  const removeLabel = t('equipment:actions.remove_file');

  const emptyState = (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={4} color="text.secondary">
      <AttachFileIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="body1">{t('equipment:messages.no_files')}</Typography>
      {canUpload && <Typography variant="body2">{t('equipment:messages.no_files_hint')}</Typography>}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {picker.input}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ flex: 1, minHeight: 400, overflow: 'hidden' }}>
            <FileExplorer
              files={files}
              folders={[]}
              layout="full"
              enableSearch={files.length > 5}
              enablePreview={true}
              enableViewToggle={true}
              enableUploadFile={canUpdate && canUpload}
              onUploadFile={picker.open}
              onChooseFile={canBrowseFiles ? handleOpenDialog : undefined}
              onFileAction={canUpdate ? handleFileAction : undefined}
              fileActions={canUpdate ? ['download', { key: 'remove', label: removeLabel }] : ['download']}
              deleteLabel={removeLabel}
              selectionMode="single"
              emptyState={emptyState}
              onFilesDropped={canUpload ? uploads.enqueue : undefined}
              dropLabel={t('equipment:messages.drop_files', { name: equipment?.name })}
            />
          </Box>
        )}
      </Box>

      <SelectFileDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelectFile={handleAttachFiles}
        multiSelect={true}
        excludedFileIds={alreadyAttachedFileIds}
        allowUpload={canUpload}
        title={t('equipment:actions.choose_existing_file')}
        loading={attaching}
        startingFolderId={browseFolder?.folderId ?? null}
        initialBreadcrumbs={browseFolder?.breadcrumbs ?? null}
      />

      <UploadProgressList
        variant="floating"
        items={uploads.items}
        onDismiss={uploads.dismiss}
        onClose={uploads.clearFinished}
      />
    </>
  );
}
